<template>
  <div>
    <div
      v-if="options.length"
      class="ls-service-item-options--circle"
      roll="button"
      test-id="menu"
      @click.stop="onMenuClick"
    >
      <!-- render 3 dots -->
      <div v-for="dot in [1, 2, 3]" :key="dot" />
    </div>
    <transition name="fade">
      <div
        v-if="showMenu"
        v-on-clickaway="() => (showMenu = false)"
        class="ls-service-item-options--menu"
      >
        <div
          v-for="option in options"
          :key="option.title"
          test-id="menu-item"
          @click="onClick({ option })"
        >
          <span>{{ option.title }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import { lsTrackingService } from 'ls-vue-common';
import { LsServiceItemQuickActionsConstants } from './LsServiceItemQuickActions.constants';

export default {
  name: 'LsServiceItemQuickActions',
  mixins: [clickaway],
  props: {
    schedule: VueTypes.shape({
      property_id: VueTypes.number.isRequired,
      schedule_id: VueTypes.number.isRequired,
      service: VueTypes.shape({
        is_ltrs: VueTypes.bool.isRequired,
        str_id: VueTypes.string.isRequired,
      }).loose,
      type: VueTypes.oneOf(['paw', 'qualityassurance', 'manualquote', 'service', 'instant_quote'])
        .isRequired,
      instant_quote: VueTypes.shape({
        type: VueTypes.string.isRequired,
        instant_quote_bush_trimming: VueTypes.shape({
          service: VueTypes.shape({
            is_ltrs: VueTypes.bool.isRequired,
          }).isRequired.loose,
        }).loose,
        instant_quote_leaf_removal: VueTypes.shape({
          service: VueTypes.shape({
            is_ltrs: VueTypes.bool.isRequired,
          }).isRequired.loose,
        }).loose,
      }).loose,
      manualquote: VueTypes.shape({
        service: VueTypes.shape({
          is_ltrs: VueTypes.bool.isRequired,
        }).isRequired.loose,
      }).loose,
    }).isRequired.loose,
  },
  data() {
    return {
      showMenu: false,
    };
  },
  computed: {
    isMowing() {
      return _.get(this.schedule, 'service.str_id') === 'mowing';
    },
    isLtrs() {
      return _.get(this.schedule, 'service.is_ltrs');
    },
    isInstantQuote() {
      return this.schedule.type === 'instant_quote';
    },
    isQaOrPawOrService() {
      return (
        this.schedule.type === 'qualityassurance' ||
        this.schedule.type === 'paw' ||
        this.schedule.type === 'service'
      );
    },
    options() {
      const {
        serviceHistory,
        instantQuoteDetails,
        manualQuoteDetails,
        longGrassFee,
        trustFee,
        threeCutPolicy,
        skipService,
        pauseService,
        contactSupport,
      } = _.cloneDeep(LsServiceItemQuickActionsConstants);

      const params = {
        property_id: _.toString(this.schedule.property_id),
        schedule_id: _.toString(this.schedule.schedule_id),
      };

      serviceHistory.route.params = params;
      pauseService.route.params = params;

      if (this.isMowing) {
        const mowingOptions =
          this.schedule.completed_job_count < 3
            ? [longGrassFee, threeCutPolicy, trustFee, serviceHistory]
            : [skipService, pauseService, longGrassFee, serviceHistory];

        return this.addMessageCrewIfHasContractor(mowingOptions);
      }

      if (this.isLtrs) {
        return [serviceHistory, contactSupport];
      }

      if (this.isQaOrPawOrService) {
        return this.addMessageCrewIfHasContractor([serviceHistory]);
      }

      manualQuoteDetails.route.params = params;
      instantQuoteDetails.route.params = params;

      const quoteOptions = [this.isInstantQuote ? instantQuoteDetails : manualQuoteDetails];

      return this.addMessageCrewIfHasContractor(quoteOptions);
    },
  },
  methods: {
    addMessageCrewIfHasContractor(optionsArray) {
      if (_.get(this.schedule, 'contractor')) {
        const { messageCrew } = LsServiceItemQuickActionsConstants;

        optionsArray.unshift(messageCrew);
      }

      return optionsArray;
    },
    onMenuClick() {
      this.showMenu = !this.showMenu;

      // only track when menu is opened
      if (this.showMenu) {
        lsTrackingService.track({
          event: 'click',
          target: 'ls_service_item_quick_actions-open_menu',
        });
      }
    },
    onClick({ option }) {
      this.showMenu = false;
      lsTrackingService.track({ event: 'click', target: option.trackId });

      if (_.get(option, 'url')) {
        return window.open(option.url);
      }
      this.$router.push(option.route);
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.15s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.ls-service-item-options--circle {
  height: 36px;
  width: 36px;
  border-radius: 50%;
  background-color: $primary-light;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  // dots
  > div {
    height: 4px;
    width: 4px;
    border-radius: 50%;
    background-color: $primary;
    margin: 0 1px;
  }
}

.ls-service-item-options--menu {
  position: absolute;
  top: 50px;
  right: -1px;
  border: 2px solid $primary-light;
  width: 240px;
  background-color: $white;
  z-index: $z-index-loading-overlay;

  /* up arrow with border */
  &:before {
    border-bottom: 9px solid $primary-light;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    content: '';
    position: absolute;
    right: 8px;
    top: -11px;
  }
  &:after {
    border-bottom: 8px solid $white;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    content: '';
    position: absolute;
    right: 8px;
    top: -8px;
  }
  /* end up arrow with border */

  > div {
    cursor: pointer;
    padding: 0px 20px 0 18px;
    position: relative;

    > span {
      display: block;
      padding: 10px 0;
      user-select: none;
      font-size: $size-5;
      font-weight: normal;
      color: $gray-primary;
    }

    &:not(:last-of-type) {
      ::before {
        position: absolute;
        bottom: 0;
        height: 1px;
        background-color: $primary-light;
        content: '';
        display: block;
        margin: 0 auto;
        width: 84%;
        @media (max-width: $mobile-max-width) {
          width: 93%;
        }
      }
    }

    &:hover {
      background-color: $primary;
      > span {
        color: $primary-light;
      }
      ::before {
        content: '';
        position: absolute;
        height: 1px;
        top: -1px;
        background-color: $white;
        width: 100%;
        left: 0;
      }

      &:first-of-type {
        ::before {
          background-color: $primary-light;
        }
      }
    }
  }

  @media (max-width: $mobile-max-width) {
    width: calc(100% + 2px);
  }
}

.themed-app {
  .ls-service-item-options--circle {
    background-color: $theme-color-primary-light;
    > div {
      background-color: $theme-color-primary;
    }
  }
  .ls-service-item-options--menu {
    border: 2px solid $theme-color-primary-light;
    background-color: $theme-color-white;
    &:before {
      border-bottom: 9px solid $theme-color-primary-light;
    }
    &:after {
      border-bottom: 8px solid $theme-color-white;
    }
    > div {
      > span {
        font-size: var(--theme-txt-body);
        color: $theme-color-gray-darker;
      }
      &:not(:last-of-type) {
        ::before {
          background-color: $theme-color-primary-light;
        }
      }
      &:hover {
        background-color: $theme-color-primary;
        > span {
          color: $theme-color-primary-light;
        }
        ::before {
          background-color: $theme-color-white;
        }
        &:first-of-type {
          ::before {
            background-color: $theme-color-primary-light;
          }
        }
      }
    }
  }
}
</style>
